import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { adminService, SystemMetrics } from '../../services/adminService';
import { AdminClaimButton } from '../../components/AdminClaimButton';

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics>({
    totalUsers: 0,
    activeUsers: 0,
    totalApiaries: 0,
    totalHives: 0,
    totalInspections: 0,
    beebaseAlerts: 0,
    bugReports: 0
  });
  
  const [bugReports, setBugReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadAdminMetrics = async () => {
      setLoading(true);
      try {
        console.log('🔄 AdminDashboard: Starting to load metrics...');
        
        const [metrics, reports] = await Promise.all([
          adminService.getSystemMetrics(),
          adminService.getBugReports()
        ]);
        
        console.log('✅ AdminDashboard: Metrics loaded:', metrics);
        setSystemMetrics(metrics);
        setBugReports(reports);
      } catch (error) {
        console.error('❌ AdminDashboard: Error loading admin metrics:', error);
        // Show a more helpful error message to the user
        if (error instanceof Error && (error.message?.includes('permission') || error.message?.includes('denied'))) {
          console.error('💡 This might be a permissions issue. Try logging out and back in.');
        }
        // Fallback to zero values if there's an error
        setSystemMetrics({
          totalUsers: 0,
          activeUsers: 0,
          totalApiaries: 0,
          totalHives: 0,
          totalInspections: 0,
          beebaseAlerts: 0,
          bugReports: 0
        });
        setBugReports([]);
      } finally {
        setLoading(false);
      }
    };

    loadAdminMetrics();
  }, []);

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ 
        marginBottom: '2rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '2rem',
        borderRadius: '12px'
      }}>
        <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2.5rem' }}>🛠️ Admin Dashboard</h1>
        <p style={{ margin: '0 0 1rem 0', fontSize: '1.2rem', opacity: 0.9 }}>
          Welcome, {user?.name}! System overview and analytics for HiveTrack UK
        </p>
        {/* Temporary admin claim button */}
        <AdminClaimButton />
      </div>
      
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #ecf0f1' }}>
        <button 
          style={{
            padding: '0.75rem 1.5rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'overview' ? '3px solid #f39c12' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '1rem',
            color: activeTab === 'overview' ? '#f39c12' : 'inherit'
          }}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          style={{
            padding: '0.75rem 1.5rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'reports' ? '3px solid #f39c12' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '1rem',
            color: activeTab === 'reports' ? '#f39c12' : 'inherit'
          }}
          onClick={() => setActiveTab('reports')}
        >
          🐛 Bug Reports
        </button>
        <button 
          style={{
            padding: '0.75rem 1.5rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'analytics' ? '3px solid #f39c12' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '1rem',
            color: activeTab === 'analytics' ? '#f39c12' : 'inherit'
          }}
          onClick={() => setActiveTab('analytics')}
        >
          📈 Analytics
        </button>
        <button 
          style={{
            padding: '0.75rem 1.5rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'system' ? '3px solid #f39c12' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '1rem',
            color: activeTab === 'system' ? '#f39c12' : 'inherit'
          }}
          onClick={() => setActiveTab('system')}
        >
          ⚙️ System
        </button>
      </div>
      
      {activeTab === 'overview' && (
        <div>
          <h2 style={{ color: '#2c3e50', marginBottom: '2rem' }}>📊 System Overview</h2>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>👥</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Users</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.totalUsers}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Registered beekeepers</p>
            </div>
            
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>🟢</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Users</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.activeUsers}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Active in last 30 days</p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>📍</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Apiaries</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.totalApiaries}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Apiary locations tracked</p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>🏠</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Hives</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.totalHives}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Individual hives managed</p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>📝</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Inspections</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.totalInspections}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Total inspections logged</p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed',
              borderLeft: '4px solid #e74c3c'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>🚨</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>BeeBase Alerts</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#e74c3c', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.beebaseAlerts}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Current active alerts</p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed',
              borderLeft: '4px solid #9b59b6'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>🐛</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bug Reports</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#9b59b6', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.bugReports}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Total user reports</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div>
          <h2 style={{ color: '#2c3e50', marginBottom: '2rem' }}>🐛 Bug Reports</h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '1.2rem', color: '#7f8c8d' }}>Loading bug reports...</div>
            </div>
          ) : bugReports.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '3rem',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>🎉</span>
              <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>No Bug Reports</h3>
              <p style={{ color: '#7f8c8d', margin: 0 }}>Great! No users have reported any issues yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {bugReports.map((report) => (
                <div key={report.id} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid #e1e8ed'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '1.1rem' }}>
                        Bug Report #{report.id.slice(-8)}
                      </h4>
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: '#7f8c8d' }}>
                        <span>👤 {report.userEmail || 'Anonymous'}</span>
                        <span>📅 {report.createdAt.toLocaleDateString()}</span>
                        <span>📍 {report.pagePath}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <h5 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>Message:</h5>
                    <p style={{ margin: 0, color: '#4a5568', lineHeight: 1.5 }}>
                      {report.message || 'No message provided'}
                    </p>
                  </div>

                  {report.screenshotUrl && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h5 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>Screenshot:</h5>
                      <img 
                        src={report.screenshotUrl} 
                        alt="Bug report screenshot"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          border: '1px solid #e1e8ed',
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(report.screenshotUrl, '_blank')}
                      />
                    </div>
                  )}

                  <div style={{ fontSize: '0.8rem', color: '#7f8c8d', borderTop: '1px solid #f1f1f1', paddingTop: '0.5rem' }}>
                    <strong>User Agent:</strong> {report.userAgent || 'Unknown'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div>
          <h2 style={{ color: '#2c3e50', marginBottom: '2rem' }}>📈 Analytics</h2>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>📊</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Average Hives per User</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.totalUsers > 0 ? Math.round((systemMetrics.totalHives / systemMetrics.totalUsers) * 10) / 10 : 0}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Hives per beekeeper</p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>📝</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Inspections per Hive</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.totalHives > 0 ? Math.round((systemMetrics.totalInspections / systemMetrics.totalHives) * 10) / 10 : 0}
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Average inspections</p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>👁️</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>User Engagement</h3>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#27ae60', marginBottom: '0.5rem' }}>
                {loading ? '...' : systemMetrics.totalUsers > 0 ? Math.round((systemMetrics.activeUsers / systemMetrics.totalUsers) * 100) : 0}%
              </div>
              <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0' }}>Active user percentage</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'system' && (
        <div>
          <h2 style={{ color: '#2c3e50', marginBottom: '2rem' }}>⚙️ System Health</h2>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed',
              borderLeft: '4px solid #27ae60'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>🟢</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Database Status</h3>
              <p style={{ color: '#27ae60', fontSize: '1.1rem', margin: '0.5rem 0' }}>Firestore: Operational</p>
              <small style={{ color: '#7f8c8d' }}>All collections accessible</small>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed',
              borderLeft: '4px solid #27ae60'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>☁️</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Storage</h3>
              <p style={{ color: '#27ae60', fontSize: '1.1rem', margin: '0.5rem 0' }}>Firebase Storage: Operational</p>
              <small style={{ color: '#7f8c8d' }}>Images and files uploading</small>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e1e8ed',
              borderLeft: '4px solid #27ae60'
            }}>
              <span style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}>🔐</span>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Authentication</h3>
              <p style={{ color: '#27ae60', fontSize: '1.1rem', margin: '0.5rem 0' }}>Firebase Auth: Operational</p>
              <small style={{ color: '#7f8c8d' }}>User authentication working</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
